import { Api } from "@Shared/schemas";
import { CreatePlaySessionRequest } from "@Shared/schemas/sessions/playSessionSchemas";
import { UserProgression } from "@Shared/schemas/progression/progressionSchemas";
import { useApiMutation } from "@hooks";
import { ITrainingPlanNavigationState } from "@Components/training-plans/training-plans-drawer/training-plans-drawer.utils";
import { DEFAULT_POSITION_SELECTOR_CONFIG, flattenPositionConfig } from "@Constants/game/position-config";
import getLeagueInfo from "@Containers/shared/helpers/get-league-info";
import { Actions, AppContext, Events, UnityBridge } from "@Context/common/general/context";
import GameModes from "@Enums/GameModes";
import MetricEventTypes from "@Enums/MetricEventTypes";
import PhaseOfPlay from "@Enums/PhaseOfPlay";
import PlayPositions from "@Enums/PlayPositions";
import PlaySessionTypes from "@Enums/PlaySessionTypes";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import { Division, League } from "@Globals/types";
import { loadPhasesFromStorage } from "@Helpers/phase-storage";
import { loadPositionsFromStorage, savePositionsToStorage } from "@Helpers/position-storage";
import useCompletePlaySession from "@hooks/game/use-complete-play-session";
import useI18n from "@i18n";
import { isNumber } from "lodash-es";
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

type PracticeSessionOptions = Omit<NonNullable<CreatePlaySessionRequest["practise_session_config"]>, "rating"> & {
  game_speed: number;
  scenario_count: number;
  rating?: number | null;
};

export interface UsePractiseResponse {
  playerRating: number | null;
  playersDivision: Division | undefined;
  allDivisions: Division[];
  selectedLeague: League | undefined;
  selectedDivision: Division | undefined;
  isSelectionLocked: boolean;
  progressionConfig: UserProgression["progression_config"] | null;
  sessionOptions: PracticeSessionOptions;
  onRatingSelected: (rating: number) => void;
  onPractiseSessionStart: () => void;
  setSessionOptions: Dispatch<SetStateAction<PracticeSessionOptions>>;
  isAboveMax: boolean;
  selectedPositions: PlayPositions[];
  selectedPhases: PhaseOfPlay[];
  setSelectedPositions: (positions: PlayPositions[]) => void;
  setSelectedPhases: (phases: PhaseOfPlay[]) => void;
  isCompletingSession: boolean;
}

const defaultPracticePositions = flattenPositionConfig(DEFAULT_POSITION_SELECTOR_CONFIG.positions).map(position => position.id);
const getValidPracticePositions = (positions: PlayPositions[]) => positions.filter(position => defaultPracticePositions.includes(position));
const loadPracticePositionsFromStorage = () => getValidPracticePositions(loadPositionsFromStorage());

export const usePractise = (): UsePractiseResponse => {
  const { state, dispatch } = useContext(AppContext);
  const { i18n } = useI18n();
  const createPlaySession = useApiMutation({ api: Api.Sessions.CreatePlaySession });
  const location = useLocation();
  const locationState = (location.state as ITrainingPlanNavigationState | null) ?? null;
  const trainingPlanLaunchConfig =
    locationState?.trainingPlanLaunchConfig?.source === "training-plan" && locationState.trainingPlanLaunchConfig.playMode === "practice"
      ? locationState.trainingPlanLaunchConfig
      : null;

  const [selectedPositions, setSelectedPositions] = useState<PlayPositions[]>(loadPracticePositionsFromStorage());
  const [selectedPhases, setSelectedPhases] = useState<PhaseOfPlay[]>([]);
  const [positionsInitialized, setPositionsInitialized] = useState(false);
  const [phasesInitialized, setPhasesInitialized] = useState(false);

  const [playerRating] = useState<number | null>(state.user.userRating);
  const [playersDivision, setPlayersDivision] = useState<Division>();
  const [allDivisions, setAllDivisions] = useState<Division[]>([]);

  const [selectedLeague, setSelectedLeague] = useState<League>();
  const [selectedDivision, setSelectedDivision] = useState<Division>();
  const [isSelectionLocked, setIsSelectionLocked] = useState<boolean>(false);

  const [progressionConfig, setProgressionConfig] = useState<UserProgression["progression_config"] | null>(null);

  const [sessionOptions, setSessionOptions] = useState<PracticeSessionOptions>({
    game_speed: 100,
    scenario_count: trainingPlanLaunchConfig?.scenarioCount ?? 5,
    rating: null,
    awareness_tests: true,
    bullet_time_decisions: false,
  });

  const ratingLimit = 1000;

  // Initialize positions from storage
  useEffect(() => {
    const storedPositions = loadPositionsFromStorage();
    const validPositions = getValidPracticePositions(storedPositions);
    if (storedPositions.length !== validPositions.length) {
      savePositionsToStorage(validPositions);
    }
    const initialPositions = trainingPlanLaunchConfig?.playPosition ? [trainingPlanLaunchConfig.playPosition] : validPositions || [];
    setSelectedPositions(initialPositions);
    setPositionsInitialized(true);
  }, [trainingPlanLaunchConfig?.playPosition, location.key]);

  // Initialize phases from storage
  useEffect(() => {
    const storedPhases = loadPhasesFromStorage();
    const initialPhases = trainingPlanLaunchConfig?.phase ? [trainingPlanLaunchConfig.phase] : storedPhases || [];
    setSelectedPhases(initialPhases);
    setPhasesInitialized(true);
  }, [trainingPlanLaunchConfig?.phase, location.key]);

  useEffect(() => {
    if (typeof trainingPlanLaunchConfig?.scenarioCount !== "number") {
      return;
    }

    setSessionOptions(previousOptions => ({
      ...previousOptions,
      scenario_count: trainingPlanLaunchConfig.scenarioCount!,
    }));
  }, [trainingPlanLaunchConfig?.scenarioCount, location.key]);

  useEffect(() => {
    if (!positionsInitialized) return;
    setSessionOptions(prev => ({
      ...prev,
      play_positions: selectedPositions.length > 0 ? selectedPositions : undefined,
    }));
  }, [selectedPositions, positionsInitialized]);

  useEffect(() => {
    if (!phasesInitialized) return;
    setSessionOptions(prev => ({
      ...prev,
      phases_of_play: selectedPhases.length > 0 ? selectedPhases : undefined,
    }));
  }, [selectedPhases, phasesInitialized]);

  useEffect(() => {
    if (!isNumber(playerRating)) return;
    if (isNumber(state.user.userRating)) {
      setSessionOptions({ ...sessionOptions, rating: state.user.userRating });
    }
    dispatch({
      type: Actions.ProgressionActions.GetProgressionConfig,
      cacheTime: [120, "mins"],
      disableLoaders: false,
      onSuccess: data => {
        setProgressionConfig(data);
        const leagueInfo = getLeagueInfo(data.leagues, playerRating);
        setPlayersDivision(leagueInfo?.division || undefined);
        setAllDivisions(data.leagues.map((league: League) => league.divisions).flat());
        setSessionOptions({
          ...sessionOptions,
          game_speed: leagueInfo.division?.play_item_config.game_speed || 100,
          awareness_tests: leagueInfo.division?.score_config[MetricEventTypes.AWARENESS] ? true : false,
          rating: leagueInfo.division?.rating_point,
        });
      },
    });
  }, [state.user.userRating]);

  useEffect(() => {
    if (!isNumber(sessionOptions.rating) || !progressionConfig) return;
    const leagueInfo = getLeagueInfo(progressionConfig.leagues, sessionOptions.rating);
    leagueInfo.league && setSelectedLeague(leagueInfo.league);
    leagueInfo.division && setSelectedDivision(leagueInfo.division);
    setSessionOptions(prev => ({
      ...prev,
      game_speed: leagueInfo.division?.play_item_config.game_speed || 100,
    }));
  }, [sessionOptions.rating, progressionConfig]);

  const showPlaylistResults = (resultsData: { play_session_id: number }) => {
    Events.app.navigate.emit({
      path: `/home/practise/results/${resultsData.play_session_id}`,
      data: resultsData,
    });
  };

  const { isCompletingSession } = useCompletePlaySession({
    onSuccess: (_, playSessionId) => showPlaylistResults({ play_session_id: playSessionId }),
  });

  useEffect(() => {
    Events.app.showHeaderButton.emit({ type: VRHeaderButtonType.BACK, title: i18n("session-type.practise_session") });
    return () => Events.app.hideHeaderButton.emit();
  }, []);

  const onPractiseSessionStart = () => {
    if (createPlaySession.isPending) return;
    createPlaySession.mutate(
      {
        account_id: state.account?.id ?? null,
        session_type: PlaySessionTypes.PRACTISE_SESSION,
        game_mode: GameModes.SCENARIO,
        practise_session_config: { ...sessionOptions, rating: sessionOptions.rating ?? undefined },
      },
      {
        onSuccess: data => {
          UnityBridge.dispatchWebEvent({
            type: UnityBridge.OutgoingWebEvents.StartPlaylist,
            payload: { items: data.play_items, play_session_id: data.play_session_id },
          });
        },
      }
    );
  };

  const onRatingSelected = (rating: number) => {
    setSessionOptions({ ...sessionOptions, rating: rating });

    if (!playersDivision) return;

    setIsSelectionLocked(rating > playersDivision?.rating_point + ratingLimit);
  };

  const isAboveMax = sessionOptions.rating && playersDivision ? sessionOptions.rating > playersDivision.rating_point + ratingLimit : false;

  return {
    playerRating,
    playersDivision,
    allDivisions,
    selectedLeague,
    selectedDivision,
    isSelectionLocked,
    progressionConfig,
    sessionOptions,
    onRatingSelected,
    onPractiseSessionStart,
    setSessionOptions,
    isAboveMax,
    selectedPositions,
    selectedPhases,
    setSelectedPositions,
    setSelectedPhases,
    isCompletingSession,
  };
};
